import { toast } from "sonner";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { tableWrapperClassName } from "@/components/common/Table/TableStyles";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { Copy, Download, HelpCircle, Link } from "lucide-react";
import React from 'react';
import { LatestPublicVersion, ServiceVersionRepository, ServiceVersion } from "../repositories/ServiceVersionsRepository";
import ReleaseNotesLink from "../components/Docs/ReleaseNotesLink";
import DocsHelpLink from "@/components/Docs/DocsHelpLink";

type state = "loading" | "error" | "uninitiated";

const downloadBuild = async (repo: ServiceVersionRepository, blobName: string, onMissing?: () => void) => {
  try {
    window.open(await repo.getDownloadUrl(blobName), "_blank");
  } catch (error: any) {
    toast.error(error?.message ?? "Failed to download the file.");
    onMissing?.();
  }
};

/**
 * Copies a link to a build. The storage is private, so the link is signed: it works for anyone who has it, for one
 * hour. The clipboard gets the pending link rather than awaited text, because Safari drops the click's permission to
 * write once an await comes between the click and the write.
 */
const copyBuildLink = async (repo: ServiceVersionRepository, blobName: string) => {
  const link = repo.getDownloadUrl(blobName);
  try {
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ "text/plain": link.then((url) => new Blob([url], { type: "text/plain" })) }),
      ]);
    } catch {
      await navigator.clipboard.writeText(await link);
    }
    toast.success("Download link copied. It works for one hour.");
  } catch (error: any) {
    toast.error(error?.message ?? "Failed to copy the download link.");
  }
};

/** The build an updater, or a client with no Service Version, installs — with a download and a copyable link. */
function LatestBuild({ repo, appType, counter }: { repo: ServiceVersionRepository; appType: "client" | "updater"; counter: number }) {
  const [latest, setLatest] = React.useState<LatestPublicVersion | null | state>("loading");

  React.useEffect(() => {
    let isCancelled = false;
    setLatest("loading");
    repo.getLatestPublicVersion(appType)
      .then((version) => !isCancelled && setLatest(version))
      .catch(() => !isCancelled && setLatest("error"));
    return () => { isCancelled = true; };
  }, [repo, appType, counter]);

  return (
    <div className="flex flex-wrap items-center gap-2 py-3 text-sm">
      <span className="font-medium">Latest public build:</span>
      {latest === "loading" && <span className="text-muted-foreground">loading…</span>}
      {latest === "error" && <span className="text-muted-foreground">could not be loaded</span>}
      {latest === null && <span className="text-muted-foreground">none yet</span>}
      {latest && typeof latest === "object" && (
        <>
          <span className="font-mono">{latest.version}</span>
          <Button size="sm" variant="outline" onClick={() => downloadBuild(repo, latest.blobName)}>
            <Download className="h-4 w-4" />
            Download
          </Button>
          <Button size="sm" variant="outline" onClick={() => copyBuildLink(repo, latest.blobName)}>
            <Link className="h-4 w-4" />
            Copy link
          </Button>
        </>
      )}
    </div>
  );
}

function renderVersions(
    versions: ServiceVersion[] | state,
    isServiceVersion: boolean,
    uninited: React.ReactElement,
    repo: ServiceVersionRepository,
    refresh: () => void,
    appType: "client" | "updater"
) {
  if (typeof versions === 'string') {
    switch (true) {
      case versions === "loading":
        return <div className="w-full h-2 bg-primary animate-pulse rounded" />;
      case versions === "error":
        return <span>Error loading versions</span>;
      case versions === "uninitiated":
        return uninited;
      default:
        return <span>Unknown state {versions}</span>;
    }
  } else if (versions.length === 0) {
    return <p>Nothing to show here</p>;
  } else {
    if (!Array.isArray(versions)) {
      return <span>Invalid data format</span>;
    }
    const copyText = async (text: string) => {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    };
    const handleRecheckMissingBuild = async (id: string) => {
      if (!repo || !refresh) return;
      try {
        if (await repo.recheckMissingBuild(id)) toast.success("Build found; it is no longer marked missing");
        else toast.warning("The build file is still missing from storage");
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to recheck build");
      }
      refresh();
    };
    const handleVisibility = async (id: string, isHidden: boolean) => {
      if (!repo || !refresh) return;
      try {
        await repo.changeVisibility(id, isHidden);
        toast.success(isHidden ? "Version hidden" : "Version made public");
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to change version visibility");
      }
      refresh();
    };
    const handlePromote = async (version: ServiceVersion, isHidden: boolean) => {
      if (!repo || !refresh || !appType) return;
      try {
        if (appType === "client") {
          await repo.createServiceVersion(version.version, version.blobName, version.tag, isHidden);
        } else {
          await repo.createUpdaterVersion(version.version, version.blobName, version.tag, isHidden);
        }
        toast.success(`Version ${version.version} promoted as ${isHidden ? "private" : "public"}`);
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to promote version");
      }
      refresh();
    };
    const handleDownload = (blobName: string) => repo && downloadBuild(repo, blobName, refresh);
    return (
      <div className={tableWrapperClassName}>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Version</TableHead>
            <TableHead>Blob Name</TableHead>
            <TableHead>Tag</TableHead>
            {isServiceVersion && (
              <>
                <TableHead>Blacklisted Count</TableHead>
                <TableHead>Is Missing</TableHead>
                <TableHead>Is Public</TableHead>
                <TableHead></TableHead>
              </>
            )}
            {!isServiceVersion && (
              <TableHead></TableHead>
            )}
            <TableHead className="w-[50px]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {versions.map((version, index) => (
            <TableRow key={index}>
              <TableCell>
                <span className="flex items-center gap-1">
                  {version.version}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button title='Copy' onClick={() => copyText(version.version)} variant='ghost' className="px-2" size='sm'><Copy className="h-4 w-4" /></Button>
                      </TooltipTrigger>
                      <TooltipContent>Copy version</TooltipContent>
                    </Tooltip>
                    <ReleaseNotesLink component={appType} version={version.version} />
                  </TooltipProvider>
                </span>
              </TableCell>
              <TableCell>
                <span className="flex items-center gap-1">
                  {version.blobName.split('/').map((x, i) => <React.Fragment key={i}>&#8203;{i === 0 ? '' : '/'}{x}</React.Fragment>)}
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button title='Copy' onClick={() => copyText(version.blobName)} variant='ghost' className="px-2" size='sm'><Copy className="h-4 w-4" /></Button>
                      </TooltipTrigger>
                      <TooltipContent>Copy blob name</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </span>
              </TableCell>
              <TableCell>{version.tag}</TableCell>
              {isServiceVersion && (
                <>
                  <TableCell>
                    {version.failedClients > 0 ? (
                      <Badge variant="danger">{version.failedClients}</Badge>
                    ) : (
                      <Badge variant="primary">0</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {version.isBlobMissing ? (
                      <span className="flex items-center gap-1">
                        <Badge variant="warning">Yes</Badge>
                        <Button size="sm" variant="outline" onClick={() => handleRecheckMissingBuild(version.id!)}>Recheck</Button>
                      </span>
                    ) : (
                      <Badge variant="primary">No</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {version.isHidden ? (
                      <Badge variant="warning">No</Badge>
                    ) : (
                      <Badge variant="primary">Yes</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    {version.isHidden ? (
                      <Button size="sm" onClick={() => handleVisibility(version.id!, false)}>Make public</Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleVisibility(version.id!, true)}>Make private</Button>
                    )}
                  </TableCell>
                </>
              )}
              {!isServiceVersion && (
                <TableCell>
                  <div className="flex gap-1">
                    <Button disabled size="sm" variant="secondary">Promote as</Button>
                    <Button size="sm" onClick={() => handlePromote(version, false)}>Public</Button>
                    <Button size="sm" variant="secondary" onClick={() => handlePromote(version, true)}>Private</Button>
                  </div>
                </TableCell>
              )}
              <TableCell>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" disabled={version.isBlobMissing} onClick={() => handleDownload(version.blobName)}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Download</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="ghost" size="icon" disabled={version.isBlobMissing} onClick={() => copyBuildLink(repo, version.blobName)}>
                        <Link className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Copy a download link (works for one hour)</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
    );
  }
}

function HelpButton({ helpText, id }: { helpText: React.ReactNode; id: string }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="ml-2 h-6 w-6 rounded-full p-0"
              onClick={() => setOpen(!open)}
              variant="outline"
              size="icon"
              aria-label="Help"
              aria-expanded={open}
              aria-controls={id}
            >
              <HelpCircle className="h-3.5 w-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Toggle help</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <p className={`mt-2 mb-2 text-sm text-muted-foreground ${open ? 'block' : 'hidden'}`} id={id}>
        {helpText}
      </p>
    </>
  );
}

export default function VersionsPage() {
    const [versionRepo] = React.useState(new ServiceVersionRepository());

    const [counter, setCounter] = React.useState(0);

  const [clientVersions, setClientVersions] = React.useState("uninitiated" as ServiceVersion[] | state);
  const [updaterVersions, setUpdaterVersions] = React.useState("uninitiated" as ServiceVersion[] | state);

    const [candidateClientVersion, setCandidateClientVersion] = React.useState("uninitiated" as ServiceVersion[] | state);
    const [candidateUpdaterVersion, setCandidateUpdaterVersion] = React.useState("uninitiated" as ServiceVersion[] | state);
  const [versionTab, setVersionTab] = React.useState<"0" | "1">("0");

  const fetchCandidateClientVersion = () => {
    setCandidateClientVersion("loading");
    versionRepo.discoverClientVersions()
      .then((version) => setCandidateClientVersion(version))
      .catch(() => setCandidateClientVersion("error"));
  }
  const fetchCandidateUpdaterVersion = () => {
    setCandidateUpdaterVersion("loading");
    versionRepo.discoverUpdaterVersions()
      .then((version) => setCandidateUpdaterVersion(version))
      .catch(() => setCandidateUpdaterVersion("error"));
  }

  React.useEffect(() => {
    let isCancelled = false;
    setClientVersions("loading");
    setUpdaterVersions("loading");
    versionRepo
      .getAllKnownClientVersions()
      .then((versions) => !isCancelled && setClientVersions(versions))
      .catch(() => !isCancelled && setClientVersions("error"));
    versionRepo
      .getAllKnownUpdaterVersions()
      .then((versions) => !isCancelled && setUpdaterVersions(versions))
      .catch(() => !isCancelled && setUpdaterVersions("error"));
    return () => { isCancelled = true; };
  }, [versionRepo, counter]);

  const refresh = () => {
    if (candidateUpdaterVersion !== "uninitiated") {
      fetchCandidateUpdaterVersion();
    }
    if (candidateClientVersion !== "uninitiated") {
      fetchCandidateClientVersion();
    }
    setCounter(counter + 1);
  };
  return (
    <div className="w-full flex-1 min-h-0 overflow-y-auto">
      <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">Versions<DocsHelpLink to="administration/client-versions" /></h2>
      <ToggleGroup
        type="single"
        value={versionTab}
        onValueChange={(v) => { if (v) setVersionTab(v as "0" | "1"); }}
        variant="outline"
        size="sm"
        className="w-full my-1"
      >
        <ToggleGroupItem value="0" className="flex-1">NFS Client</ToggleGroupItem>
        <ToggleGroupItem value="1" className="flex-1">Updater</ToggleGroupItem>
      </ToggleGroup>
      {versionTab === "0" && (
        <div>
          <LatestBuild repo={versionRepo} appType="client" counter={counter} />
          <div>
            <h3 className="text-xl font-semibold flex items-center">
              New blobs
              <HelpButton
                id="discover-client-help"
                helpText={<>List of files on the blob storage that can be used as a new client version. <br />You can promote a version to be added to the list of known client versions.</>}
              />
            </h3>
            <div className="py-5">
              {renderVersions(
                candidateClientVersion,
                false,
                <Button onClick={fetchCandidateClientVersion}>Discover</Button>,
                versionRepo,
                refresh,
                "client"
              )}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold flex items-center">
              Known client versions
              <HelpButton
                id="known-client-help"
                helpText={<>List of all known client versions. You can select those versions in the client configuration. <br /><strong>Public versions</strong> are available to be selected in the client configuration. <br /><strong>Private versions</strong> have to be typed in as a custom version in the client configuration. <br />Latest <strong>public version</strong> is what will be used by the client if no version is specified in the configuration.</>}
              />
            </h3>
            <div className="py-5">
              {renderVersions(
                clientVersions,
                true,
                <span>initializing...</span>,
                versionRepo,
                refresh,
                "client"
              )}
            </div>
          </div>
        </div>
      )}
      {versionTab === "1" && (
        <div>
          <LatestBuild repo={versionRepo} appType="updater" counter={counter} />
          <div>
            <h3 className="text-xl font-semibold flex items-center">
              New blobs
              <HelpButton
                id="discover-updater-help"
                helpText={<>List of files on the blob storage that can be used as a new updater version. <br />You can promote a version to be added to the list of known updater versions.</>}
              />
            </h3>
            <div className="py-5">
              {renderVersions(
                candidateUpdaterVersion,
                false,
                <Button onClick={fetchCandidateUpdaterVersion}>Discover</Button>,
                versionRepo,
                refresh,
                "updater"
              )}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold flex items-center">
              Known updater versions
              <HelpButton
                id="known-updater-help"
                helpText={<>List of all known updater versions. <strong>Latest public version will always be used.</strong> <br />At the moment, there is no way to select a specific updater version in the configuration. <br />Treat making a version <strong>private</strong> as a way to disable (or rollback) it from the updater.</>}
              />
            </h3>
            <div className="py-5">
              {renderVersions(
                updaterVersions,
                true,
                <span>initializing...</span>,
                versionRepo,
                refresh,
                "updater"
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
